<template>
  <div class="ds-avisos-importantes">
    <div class="sectionTitle ds-h3">{{ $t('HOME_avisos_importantes') }}</div>

    <div v-if="hasAvisos" class="avisos-tags">
      <button
        v-for="category in avisoCategories"
        :key="category.key"
        :class="[
          'avisos-tag',
          'ds-body-medium-bold',
          `avisos-tag--${category.key}`,
          { 'is-active': isCategoryExpanded(category.key), 'is-disabled': category.count === 0 }
        ]"
        :disabled="category.count === 0"
        type="button"
        @click="toggleCategory(category.key)">
        <span class="avisos-tag__label">{{ category.label }}</span>
        <span :class="['avisos-tag__count', 'ds-body-medium-bold', `avisos-tag__count--${category.key}`]">{{ category.count }}</span>
        <span class="avisos-tag__toggle" :aria-label="category.label">
          <DsArrowUp v-if="isCategoryExpanded(category.key)" class="avisos-tag__toggle-icon" />
          <DsArrowDown v-else class="avisos-tag__toggle-icon" />
        </span>
      </button>
    </div>

    <div v-if="hasExpandedCategory" class="avisos-list">
      <div
        v-for="(aviso, idx) in paginatedAvisos"
        :key="`${expandedCategory}-${pageStartIndex + idx}`"
        :class="['aviso', avisoClass(aviso.SeverityLevel), { 'is-clickable': aviso.ActionUrl }]"
        @click="onClick(aviso.ActionUrl)">
        <div class="aviso__icon">
          <i :class="iconForSeverity(aviso.SeverityLevel)"></i>
        </div>
        <div class="aviso__text">
          <div class="ds-body-big aviso__title">{{ aviso.MessageBody }}</div>
        </div>
      </div>

      <div v-if="showPagination" class="avisos-pagination">
        <button
          class="avisos-pagination__button"
          type="button"
          :disabled="currentPage === 1"
          aria-label="Pagina anterior"
          @click="goToPreviousPage">
          <i class="el-icon-arrow-left"></i>
        </button>

        <button
          v-for="page in pageNumbers"
          :key="page"
          :class="['avisos-pagination__button', 'avisos-pagination__page', 'ds-body-medium', { 'is-active': page === currentPage }]"
          type="button"
          :aria-current="page === currentPage ? 'page' : null"
          @click="goToPage(page)">
          {{ page }}
        </button>

        <button
          class="avisos-pagination__button"
          type="button"
          :disabled="currentPage === totalPages"
          aria-label="Pagina siguiente"
          @click="goToNextPage">
          <i class="el-icon-arrow-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import DsArrowDown from './DsArrowDown'
import DsArrowUp from './DsArrowUp'

export default {
  name: 'DsAvisosImportantes',
  components: { DsArrowDown, DsArrowUp },
  props: {
    avisos: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  data() {
    return {
      expandedCategory: null,
      currentPage: 1,
      pageSize: 3
    }
  },
  computed: {
    availableAvisos() {
      if (Array.isArray(this.avisos)) return this.avisos
      return []
    },
    hasAvisos() {
      return this.availableAvisos.length > 0
    },
    sortedAvisos() {
      const order = { error: 0, warning: 1, info: 2 }
      return this.availableAvisos.slice().sort((a, b) => {
        const sa = this.normalizeSeverity(a.SeverityLevel)
        const sb = this.normalizeSeverity(b.SeverityLevel)
        const oa = typeof order[sa] !== 'undefined' ? order[sa] : 3
        const ob = typeof order[sb] !== 'undefined' ? order[sb] : 3
        return oa - ob
      })
    },
    groupedAvisos() {
      return this.sortedAvisos.reduce((groups, aviso) => {
        const severity = this.normalizeSeverity(aviso.SeverityLevel)
        groups[severity].push(aviso)
        return groups
      }, {
        error: [],
        warning: [],
        info: []
      })
    },
    avisoCategories() {
      return [
        {
          key: 'error',
          label: this.$t('HOME_avisos_alertas'),
          count: this.groupedAvisos.error.length
        },
        {
          key: 'warning',
          label: this.$t('HOME_avisos_advertencias'),
          count: this.groupedAvisos.warning.length
        },
        {
          key: 'info',
          label: this.$t('HOME_avisos_informacion'),
          count: this.groupedAvisos.info.length
        }
      ]
    },
    hasExpandedCategory() {
      return this.isCategoryAvailable(this.expandedCategory)
    },
    filteredAvisos() {
      if (!this.hasExpandedCategory) return []
      return this.groupedAvisos[this.expandedCategory]
    },
    totalPages() {
      if (!this.filteredAvisos.length) return 0
      return Math.ceil(this.filteredAvisos.length / this.pageSize)
    },
    pageStartIndex() {
      return (this.currentPage - 1) * this.pageSize
    },
    paginatedAvisos() {
      return this.filteredAvisos.slice(this.pageStartIndex, this.pageStartIndex + this.pageSize)
    },
    pageNumbers() {
      return Array.from({ length: this.totalPages }, (_, index) => index + 1)
    },
    showPagination() {
      return this.totalPages > 1
    }
  },
  watch: {
    avisos: {
      immediate: true,
      handler() {
        this.syncExpandedCategory()
      }
    },
    totalPages(newValue) {
      if (newValue === 0) {
        this.currentPage = 1
        return
      }

      if (this.currentPage > newValue) {
        this.currentPage = newValue
      }
    }
  },
  methods: {
    normalizeSeverity(sev) {
      const severity = (sev || '').toString().toLowerCase()
      if (severity === 'critical') return 'error'
      if (severity === 'error') return 'error'
      if (severity === 'warning') return 'warning'
      return 'info'
    },
    syncExpandedCategory() {
      if (this.isCategoryAvailable(this.expandedCategory)) return

      const hasCriticalAvisos = this.availableAvisos.some(aviso => {
        return (aviso.SeverityLevel || '').toString().toLowerCase() === 'critical'
      })
      const nextCategory = hasCriticalAvisos && this.groupedAvisos.error.length > 0 ? 'error' : null

      if (this.expandedCategory !== nextCategory) {
        this.expandedCategory = nextCategory
      }

      this.currentPage = 1
    },
    isCategoryAvailable(categoryKey) {
      if (!categoryKey) return false
      return this.groupedAvisos[categoryKey] && this.groupedAvisos[categoryKey].length > 0
    },
    isCategoryExpanded(categoryKey) {
      return this.expandedCategory === categoryKey && this.isCategoryAvailable(categoryKey)
    },
    toggleCategory(categoryKey) {
      if (!this.isCategoryAvailable(categoryKey)) return
      if (this.expandedCategory === categoryKey) {
        this.expandedCategory = null
        this.currentPage = 1
        return
      }
      this.expandedCategory = categoryKey
      this.currentPage = 1
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages) return
      this.currentPage = page
    },
    goToPreviousPage() {
      if (this.currentPage === 1) return
      this.currentPage -= 1
    },
    goToNextPage() {
      if (this.currentPage === this.totalPages) return
      this.currentPage += 1
    },
    avisoClass(sev) {
      const s = this.normalizeSeverity(sev)
      if (s === 'error') return 'aviso--error'
      if (s === 'warning') return 'aviso--warning'
      return 'aviso--info'
    },
    iconForSeverity(sev) {
      const s = this.normalizeSeverity(sev)
      if (s === 'error') return 'el-icon-warning'
      if (s === 'warning') return 'el-icon-warning'
      return 'el-icon-info'
    },
    onClick(actionUrl) {
      if (!actionUrl) return
      // If it's an absolute URL, open in new tab; otherwise use router if available
      try {
        const isAbsolute = /^(https?:)?\/\//i.test(actionUrl)
        if (isAbsolute) {
          window.open(actionUrl, '_blank')
        } else if (this.$router) {
          this.$router.push(actionUrl).catch(() => { window.location.href = actionUrl })
        } else {
          window.location.href = actionUrl
        }
      } catch (e) {
        window.location.href = actionUrl
      }
    }
  }
}
</script>

<style scoped>
.ds-avisos-importantes {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
}

.ds-avisos-importantes .sectionTitle {
  margin-bottom: -8px;
}

.avisos-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
}

.avisos-tag {
  display: flex;
  padding: 4px 8px;
  align-items: center;
  gap: 4px;
  border-radius: 20px;
  border: 1px solid var(--Neutral-100, #F4F4F5);
  background: var(--Neutral-100, #F4F4F5);
  color: var(--Neutral-600, #52525B);
  appearance: none;
  transition: border-color 0.15s ease, background-color 0.15s ease, opacity 0.15s ease;
  cursor: pointer;
}

.avisos-tag.is-active {
  background: var(--Neutral-100, #F4F4F5);
}

.avisos-tag--error.is-active {
  border-color: var(--Error-400, #D92D20);
}

.avisos-tag--warning.is-active {
  border-color: var(--Warning-600, #F59E0B);
}

.avisos-tag--info.is-active {
  border-color: var(--Marca-600, #3B82F6);
}

.avisos-tag.is-disabled {
  opacity: 0.55;
  cursor: default;
}

.avisos-tag__label {
  color: var(--Neutral-600, #52525B);
}

.avisos-tag__count {
  display: flex;
  min-width: 22px;
  padding: 0 5px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 999px;
  color: var(--Neutral-0, #FFF);
  text-align: center;
}

.avisos-tag__count--error {
  background: var(--Error-400, #D92D20);
}

.avisos-tag__count--warning {
  background: var(--Warning-600, #F59E0B);
}

.avisos-tag__count--info {
  background: var(--Marca-600, #3B82F6);
}

.avisos-tag__toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  color: var(--Neutral-600, #52525B);
}

.avisos-tag.is-active .avisos-tag__toggle {
  color: #A1A1AA;
}

.avisos-tag__toggle-icon {
  display: block;
  width: 18px;
  height: 18px;
}

.avisos-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.avisos-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;
}

.avisos-pagination__button {
  display: flex;
  width: 32px;
  height: 32px;
  padding: 10px 0;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border: 0;
  border-radius: 4px;
  background: #FAFAFA;
  color: #606060;
  appearance: none;
  cursor: pointer;
}

.avisos-pagination__button:disabled {
  opacity: 0.45;
  cursor: default;
}

.avisos-pagination__page {
  text-align: center;
}

.avisos-pagination__page.is-active {
  background: #B7B7B7;
  color: #FFF;
}

.avisos-pagination__button .el-icon-arrow-left,
.avisos-pagination__button .el-icon-arrow-right {
  font-size: 14px;
}

.aviso {
  display: flex;
  padding: 16px;
  align-items: flex-start;
  gap: 5px;
  align-self: stretch;
  border-radius: 8px;
  box-sizing: border-box;
  transition: background-color 0.15s ease;
}

.aviso__icon {
  flex: 0 0 auto;
  font-size: 20px;
  line-height: 1;
  display: flex;
  align-items: flex-start;
}

.aviso__text {
  flex: 1 0 0;
  min-width: 0;
  text-align: left;
}

.aviso__title {
  font-weight: 400;
  color: var(--ds-color-neutral-600, #606060);
  text-align: left;
}

/* Type backgrounds only */
.aviso--error {
  background: var(--Error-50, #FFF2ED);
}
.aviso--error .aviso__icon { color: var(--Error-400, #D92D20); }

.aviso--warning {
  background: var(--Warning-50, #FEFBE8);
}
.aviso--warning .aviso__icon { color: var(--Warning-600, #F59E0B); }

.aviso--info {
  background: var(--Marca-50, #EFFAFF);
}
.aviso--info .aviso__icon { color: var(--Marca-600, #3B82F6); }

/* Hover effect when ActionUrl is present */
.is-clickable {
  cursor: pointer;
}

.is-clickable.aviso--error:hover {
  background: var(--Error-100, #FFE1D4);
}

.is-clickable.aviso--warning:hover {
  background: var(--Warning-100, #FFF6BD);
}

.is-clickable.aviso--info:hover {
  background: var(--Marca-100, #DFF3FF);
}

</style>
